import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  FormControlLabel,
  Checkbox,
  IconButton,
  Alert,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: '#1A1A1A',
    border: '1px solid #333',
    borderRadius: '12px',
    maxWidth: '800px',
    width: '90vw',
    maxHeight: '90vh',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    color: '#ccc',
  },
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': {
      borderColor: '#333',
    },
    '&:hover fieldset': {
      borderColor: '#4CAF50',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#4CAF50',
    },
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#4CAF50',
  color: '#fff',
  '&:hover': {
    backgroundColor: '#45a049',
  },
  '&:disabled': {
    backgroundColor: '#333',
    color: '#666',
  },
}));

const AnswerBox = styled(Box)(({ theme }) => ({
  border: '1px solid #333',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '12px',
  backgroundColor: 'rgba(255, 255, 255, 0.02)',
}));

const QuestionEditModal = ({ 
  open, 
  onClose, 
  question, 
  onSave, 
  answerTypeConfig 
}) => {
  const [formData, setFormData] = useState({
    questionText: '',
    timeLimit: 30,
    questionClue: '',
    answers: []
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form data when question changes
  useEffect(() => {
    if (question) {
      setFormData({
        questionText: question.questionText || '',
        timeLimit: question.timeLimit || 30,
        questionClue: question.questionClue || '',
        answers: question.answers ? [...question.answers] : []
      });
      setErrors({});
    }
  }, [question]);

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.questionText || formData.questionText.trim().length < 10) {
      newErrors.questionText = 'Question text must be at least 10 characters';
    }

    if (!formData.timeLimit || formData.timeLimit < 5 || formData.timeLimit > 300) {
      newErrors.timeLimit = 'Time limit must be between 5 and 300 seconds';
    }

    if (!formData.answers || formData.answers.length < 2) {
      newErrors.answers = 'At least 2 answers are required';
    }

    const correctAnswers = formData.answers.filter(a => a.correct).length;
    if (correctAnswers === 0) {
      newErrors.answers = 'At least one answer must be correct';
    }

    // Answer type specific validation
    if (question?.answerType === 'true_false' && formData.answers.length !== 2) {
      newErrors.answers = 'True/False questions must have exactly 2 answers';
    }

    if (question?.answerType === 'true_false' && correctAnswers !== 1) {
      newErrors.answers = 'True/False questions must have exactly one correct answer';
    }

    if (question?.answerType === 'single_choice' && correctAnswers !== 1) {
      newErrors.answers = 'Single choice questions must have exactly one correct answer';
    }

    // Validate individual answers
    formData.answers.forEach((answer, index) => {
      if (!answer.text || answer.text.trim().length < 1) {
        newErrors[`answer_${index}`] = 'Answer text is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Handle answer changes
  const handleAnswerChange = (index, field, value) => {
    const newAnswers = [...formData.answers];
    newAnswers[index] = {
      ...newAnswers[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      answers: newAnswers
    }));

    // Clear answer-specific error
    if (errors[`answer_${index}`]) {
      setErrors(prev => ({
        ...prev,
        [`answer_${index}`]: ''
      }));
    }
  };

  // Add new answer
  const addAnswer = () => {
    if (formData.answers.length < 4) {
      setFormData(prev => ({
        ...prev,
        answers: [...prev.answers, { text: '', correct: false, reason: '' }]
      }));
    }
  };

  // Remove answer
  const removeAnswer = (index) => {
    if (formData.answers.length > 2) {
      const newAnswers = formData.answers.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        answers: newAnswers
      }));
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const updatedQuestion = {
        ...question,
        ...formData
      };
      await onSave(updatedQuestion);
      onClose();
    } catch (error) {
      console.error('Error saving question:', error);
      setErrors({ general: 'Failed to save question. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  if (!question) return null;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ 
        color: '#fff', 
        borderBottom: '1px solid #333',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6">Edit Question</Typography>
        <IconButton onClick={onClose} sx={{ color: '#ccc' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {errors.general && (
          <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
            {errors.general}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Question Text */}
          <Grid item xs={12}>
            <StyledTextField
              fullWidth
              label="Question Text *"
              multiline
              rows={3}
              value={formData.questionText}
              onChange={(e) => handleFieldChange('questionText', e.target.value)}
              error={!!errors.questionText}
              helperText={errors.questionText}
            />
          </Grid>

          {/* Time Limit and Question Clue */}
          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Time Limit (seconds) *"
              type="number"
              value={formData.timeLimit}
              onChange={(e) => handleFieldChange('timeLimit', parseInt(e.target.value) || 30)}
              error={!!errors.timeLimit}
              helperText={errors.timeLimit}
              inputProps={{ min: 5, max: 300 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <StyledTextField
              fullWidth
              label="Question Clue (optional)"
              value={formData.questionClue}
              onChange={(e) => handleFieldChange('questionClue', e.target.value)}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: '#333' }} />

        {/* Answers Section */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#fff' }}>
              Answers
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addAnswer}
              disabled={formData.answers.length >= 4}
              sx={{ color: '#4CAF50' }}
            >
              Add Answer
            </Button>
          </Box>

          {errors.answers && (
            <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(244, 67, 54, 0.1)' }}>
              {errors.answers}
            </Alert>
          )}

          {formData.answers.map((answer, index) => (
            <AnswerBox key={index}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ color: '#4CAF50' }}>
                  Answer {index + 1}
                </Typography>
                {formData.answers.length > 2 && (
                  <IconButton 
                    size="small" 
                    onClick={() => removeAnswer(index)}
                    sx={{ color: '#f44336' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Answer Text *"
                    value={answer.text}
                    onChange={(e) => handleAnswerChange(index, 'text', e.target.value)}
                    error={!!errors[`answer_${index}`]}
                    helperText={errors[`answer_${index}`]}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={answer.correct}
                        onChange={(e) => handleAnswerChange(index, 'correct', e.target.checked)}
                        sx={{ color: '#4CAF50' }}
                      />
                    }
                    label="Correct Answer"
                    sx={{ color: '#fff' }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <StyledTextField
                    fullWidth
                    label="Answer Reason (optional)"
                    value={answer.reason || ''}
                    onChange={(e) => handleAnswerChange(index, 'reason', e.target.value)}
                  />
                </Grid>
              </Grid>
            </AnswerBox>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #333' }}>
        <Button onClick={onClose} sx={{ color: '#ccc' }}>
          Cancel
        </Button>
        <StyledButton 
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </StyledButton>
      </DialogActions>
    </StyledDialog>
  );
};

export default QuestionEditModal;
